/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/display-name */
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import cx from "classnames";
import React, { Fragment } from "react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { StreakIcon } from "~/components/icons/StreakIcon";
import { DEMO_FALLBACK_PORTRAITS } from "~/lib/constants";
import { randomIntFromInterval } from "~/lib/misc";

enum Variant {
  Circle,
  Rounded,
}
enum Size {
  XS,
  Small,
  Medium,
  Large,
  XL,
}

type Props = {
  variant: Variant;
  isOnline?: boolean;
  src?: string;
  fallbackText?: string;
  className?: string;
  size: Size;
  progress?: number;
  latestCheer?: string;
  streakCount?: number;
  style?: object;
};

const getFallbackImg = () => {
  const no = randomIntFromInterval(0, DEMO_FALLBACK_PORTRAITS.length - 1);
  return DEMO_FALLBACK_PORTRAITS[no];
};

const ProgressRing = ({ progress, size, styles }) => {
  return (
    <div
      className={cx(
        "absolute",
        {
          [Size.XS]: "-m-2",
          [Size.Small]: "-m-2",
          [Size.Medium]: "-m-2 lg:-m-4",
          [Size.Large]: "-m-3",
          [Size.XL]: "-m-3 lg:-m-4",
        }[size]
      )}
    >
      <CircularProgressbar
        strokeWidth={
          {
            [Size.XS]: 6,
            [Size.Small]: 6,
            [Size.Medium]: 4,
            [Size.Large]: 4,
            [Size.XL]: 4,
          }[size]
        }
        styles={buildStyles({
          pathColor: `rgba(231, 255, 1, 1)`,
          trailColor: "#0E0E0E",
          ...styles,
        })}
        value={progress}
      />
    </div>
  );
};

export const Avatar = React.memo(
  ({
    src,
    size,
    variant,
    isOnline,
    fallbackText = "Aa",
    className = "",
    progress,
    streakCount,
    latestCheer,
    style,
  }: Props) => {
    const imgSrc = src;
    return (
      <Fragment>
        <AvatarPrimitive.Root
          data-size={size}
          style={style}
          className={cx(
            "relative inline-flex",
            {
              [Size.XL]: "w-32 h-32 md:h-48 md:w-48",
              [Size.Large]: "w-[5rem] h-[5rem] md:h-32 md:w-32",
              [Size.Medium]: "h-[4.5rem] w-[4.5rem]",
              [Size.Small]: "h-10 w-10 lg:h-16 lg:w-16",
              [Size.XS]: "h-8 w-8",
            }[size],
            className
          )}
        >
          <AvatarPrimitive.Image
            src={imgSrc}
            alt="Avatar"
            className={cx(
              "h-full w-full object-cover border shadow border-neutral-200/20",
              {
                [Variant.Circle]: "rounded-full",
                [Variant.Rounded]: "rounded",
              }[variant]
            )}
          />
          {isOnline && (
            <div
              className={cx(
                "absolute bottom-0 right-0 h-4 w-4",
                {
                  [Variant.Circle]: "-translate-x-1/2 -translate-y-1/2",
                  [Variant.Rounded]: "",
                }[variant]
              )}
            >
              <span className="block h-2.5 w-2.5 rounded-full bg-volt shadow-sm" />
            </div>
          )}

          {typeof progress === "number" ? (
            <>
              <ProgressRing
                size={size}
                progress={100}
                styles={{
                  pathColor: `rgb(61 61 61)`,
                  trailColor: "#0E0E0E",
                }}
              />
              <ProgressRing
                size={size}
                progress={progress}
                styles={{
                  pathColor: `rgba(231, 255, 1, 1)`,
                  trailColor: "transparent",
                }}
              />
            </>
          ) : null}
          {streakCount || latestCheer ? (
            <div className="">
              <div
                className={cx(
                  "absolute bottom-0 right-0 flex center space-x-0 p-1 font-medium",
                  "flex",
                  {
                    [Size.XL]:
                      "lg:-translate-x-2 lg:-translate-y-4 text-black  text-sm",
                    [Size.Large]:
                      "lg:-translate-x-2 lg:-translate-y-4 text-black  text-sm",
                    [Size.Medium]:
                      "translate-x-2 translate-y-4 text-[0.75rem] text-volt ",
                    [Size.Small]:
                      "translate-x-2 translate-y-4 text-[0.75rem] text-volt ",
                    [Size.XS]:
                      "translate-x-2 translate-y-4 text-[0.5rem] text-volt ",
                  }[size]
                )}
              >
                <div
                  className={cx(
                    "absolute inset-0 border-black transform -skew-x-6",
                    "flex",
                    {
                      [Size.XL]: "bg-volt border-4 rounded-lg text-sm",
                      [Size.Large]: "bg-volt border-4 rounded-lg text-sm",
                      [Size.Medium]:
                        "text-[0.75rem] bg-neutral-800 border-2 rounded",
                      [Size.Small]:
                        "text-[0.75rem] bg-neutral-800 border-2 rounded",
                      [Size.XS]:
                        "text-[0.5rem] bg-neutral-800 border-2 rounded",
                    }[size]
                  )}
                />
                <div className="relative flex center px-1 py-[0.15rem]">
                  {latestCheer ? (
                    latestCheer
                  ) : (
                    <>
                      <span className="">{streakCount}</span>
                      <StreakIcon
                        className={
                          {
                            [Size.XL]: "w-4 h-4",
                            [Size.Large]: "",
                            [Size.XS]: "w-3 h-3",
                          }[size]
                        }
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          <AvatarPrimitive.Fallback
            className={cx(
              "flex h-full w-full items-center justify-center bg-neutral-700 shadow border border-neutral-600",
              {
                [Variant.Circle]: "rounded-full",
                [Variant.Rounded]: "rounded",
              }[variant],
              {
                [Size.XL]: "text-2xl",
                [Size.Large]: "text-xl",
                [Size.Medium]: "text-default",
                [Size.Small]: "text-sm",
                [Size.XS]: "text-xs",
              }[size]
            )}
            delayMs={600}
          >
            <span className="font-medium text-neutral-400">{fallbackText}</span>
          </AvatarPrimitive.Fallback>
        </AvatarPrimitive.Root>
      </Fragment>
    );
  }
);

// @ts-ignore
Avatar.variant = Variant;
// @ts-ignore
Avatar.size = Size;
